import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Shield, Calendar, Clock, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { pricingData } from '../../data/mock';
import coffeePosterImage from '@/assets/images/coffee-poster.png';
import { useCohortDetails } from '../../hooks/useCohortDetails';
import { SELF_PACED_INR } from '../../utils/locale';

const PricingSection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const { cohortDetails, loading: cohortLoading, isIndia } = useCohortDetails();

  // Get user's timezone for date formatting
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format cohort start date
  const formatCohortStartDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: userTimezone
    });
  };

  // Format cohort start time
  const formatCohortStartTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: userTimezone,
      timeZoneName: 'short'
    });
  };

  // Calculate end date (8 weeks from start)
  const calculateEndDate = (startDateString) => {
    if (!startDateString) return null;
    const startDate = new Date(startDateString);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (8 * 7)); // 8 weeks = 56 days
    return endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: userTimezone
    });
  };

  // Get cohort price (API may return in USD or INR based on cohort)
  const cohortPrice = cohortDetails ? parseFloat(cohortDetails.cohort_fee) : (isIndia ? 14999.00 : 499.00);
  const cohortCurrencySymbol = isIndia ? '₹' : '$';
  const cohortPriceFormatted = isIndia ? cohortPrice.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : cohortPrice.toFixed(0);

  // Get cohort start date formatted
  const cohortStartDate = cohortDetails?.start_date
    ? formatCohortStartDate(cohortDetails.start_date)
    : 'Saturday, January 31, 2026';

  // Get cohort start time
  const cohortStartTime = cohortDetails?.start_date
    ? formatCohortStartTime(cohortDetails.start_date)
    : '8pm CST';

  // Get cohort week day from start date
  const cohortWeekDay = cohortDetails?.start_date
    ? new Date(cohortDetails.start_date).toLocaleDateString('en-US', { weekday: 'long' })
    : 'Saturday';

  // Get cohort end date
  const cohortEndDate = cohortDetails?.start_date
    ? calculateEndDate(cohortDetails.start_date)
    : 'Mar 21, 2026';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="py-32 bg-[#efede6] relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <span className={`inline-block text-sm text-[#202020]/40 uppercase tracking-widest mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Simple Pricing
          </span>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-normal text-[#202020] mb-6 tracking-tight leading-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Turn your morning coffee<br />
            into a <em className="font-serif italic">Peaceful Parent Coffee</em> ritual
          </h2>
          <p className={`text-xl text-[#202020]/60 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {isIndia ? `For about ₹${Math.round(SELF_PACED_INR / 365)} a day.` : 'For about 50¢ a day.'}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Coffee Poster */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={coffeePosterImage}
                alt="A cup of peace with myself - Pour. Sip. Contemplate."
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Right - Pricing Card */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-[#202020] p-10 text-center">
                <p className="text-[#efede6]/60 text-sm uppercase tracking-widest mb-4">{pricingData.plan.name}</p>
                <div className="flex items-baseline justify-center gap-1">
                  {isIndia ? (
                    <>
                      <span className="text-6xl font-light text-[#efede6]">₹{SELF_PACED_INR.toLocaleString('en-IN')}</span>
                      <span className="text-[#efede6]/50">/year</span>
                    </>
                  ) : (
                    <>
                      <span className="text-6xl font-light text-[#efede6]">{pricingData.plan.price}¢</span>
                      <span className="text-[#efede6]/50">/{pricingData.plan.period.split(' ')[1]}</span>
                    </>
                  )}
                </div>
                <p className="text-[#efede6]/50 mt-3 text-sm italic">{isIndia ? 'One-time payment for one year access' : pricingData.plan.description}</p>
                <p className="text-[#efede6]/70 mt-8 ">
                  Many Contemplate parents don't find more time.<br />
                  They change how they sit with their coffee.
                </p>
                <p className="text-[#efede6]/50 mt-3 italic">
                  Coffee time. Contemplation time.
                </p>
                <p className="text-[#efede6]/50 mt-3 italic font-light">
                  For a relationship that means the most to you. <br /> A relationship that will stay for years to come.
                </p>
              </div>

              {/* Features */}
              <div className="p-10">
                <h4 className="font-medium text-[#202020] mb-6 text-sm uppercase tracking-wider">What you&apos;ll get:</h4>
                <ul className="space-y-4 mb-10">
                  {pricingData.plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#202020]" />
                      </div>
                      <span className="text-[#202020]/70">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => {
                    // Track button click
                    if (window.gtag) {
                      window.gtag('event', 'click', {
                        event_category: 'CTA',
                        event_label: 'Become a Peaceful Parent'
                      });
                    }
                    if (window.fbq) {
                      window.fbq('track', 'Lead');
                    }
                    navigate('/checkout');
                  }}
                  className="w-full bg-[#202020] hover:bg-[#303030] text-[#efede6] py-6 rounded-lg text-base font-medium transition-all hover:scale-[1.02] group"
                >
                  Become a Peaceful Parent
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                {/* Guarantee */}
                <div className="flex items-center justify-center gap-2 mt-6 text-[#202020]/40">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm">Cancel anytime — No contracts</span>
                </div>

                {/* Partner Discount Note */}
                <div className="flex items-center justify-center gap-2 mt-4 text-[#202020]/50">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Add your partner at checkout for <strong>50% off</strong> their fee</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Guided Cohort Option */}
        <div className={`mt-20 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-[#202020]/10">
            <div className="p-10">
              {/* Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-normal text-[#202020] mb-4 tracking-tight">
                  {cohortLoading ? (
                    'Guided Cohort: Loading...'
                  ) : (
                    `Guided Cohort: ${cohortStartDate ? `Starts ${cohortStartDate.split(',')[1]?.trim() || cohortStartDate}` : 'Starts January 31, 2026'}`
                  )}
                </h3>
                <p className="text-lg text-[#202020]/70 mb-6">
                  {cohortLoading ? (
                    'Loading cohort details...'
                  ) : (
                    `Prefer more guidance and personal support? Join the next live cohort${cohortStartDate ? ` starting ${cohortStartDate.split(',')[1]?.trim() || cohortStartDate}` : ' starting 31st January 2026'}.`
                  )}
                </p>
                <p className="text-base text-[#202020]/60 mb-4">
                  Small group of parents, trainer-led sessions, built on the same 8-week Contemplate Foundation program.
                </p>
                <p className="text-sm text-[#202020]/50 italic">
                  {cohortDetails?.max_participants && cohortDetails?.current_participants !== undefined ? (
                    `Limited seats (${cohortDetails.current_participants}/${cohortDetails.max_participants} filled) - ${cohortStartDate ? `Starts ${cohortStartDate.split(',')[1]?.trim() || cohortStartDate}` : 'Starts 31 January 2026'} - Ideal if you want accountability and direct support each week.`
                  ) : (
                    `Limited seats - ${cohortStartDate ? `Starts ${cohortStartDate.split(',')[1]?.trim() || cohortStartDate}` : 'Starts 31 January 2026'} - Ideal if you want accountability and direct support each week.`
                  )}
                </p>
              </div>

              {/* Price */}
              <div className="text-center mb-8 pb-8 border-b border-[#202020]/10">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-light text-[#202020]">
                    {cohortLoading ? '...' : `${cohortCurrencySymbol}${cohortPriceFormatted}`}
                  </span>
                </div>
                <p className="text-[#202020]/50 text-sm mt-2">One-time payment for the 8-week guided cohort</p>
              </div>

              {/* Cohort Schedule */}
              <div className="mb-8 pb-8 border-b border-[#202020]/10">
                <div className="bg-[#efede6] rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <Calendar className="w-5 h-5 text-[#202020]" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-[#202020] mb-2 flex items-center gap-2">
                        Cohort Schedule
                      </h5>
                      {cohortLoading ? (
                        <p className="text-[#202020]/70 text-sm leading-relaxed">Loading schedule...</p>
                      ) : (
                        <>
                          <p className="text-[#202020]/70 text-sm leading-relaxed">
                            Starts on <strong>{cohortStartDate || 'Saturday, January 31, 2026'}</strong>
                            {cohortStartTime && (
                              <> at <strong>{cohortStartTime}</strong></>
                            )}
                          </p>
                          <p className="text-[#202020]/70 text-sm leading-relaxed mt-2">
                            Runs for <strong>8 weeks</strong>, once a week
                          </p>
                          {cohortDetails?.meeting_link && (
                            <p className="text-[#202020]/60 text-xs mt-3 italic">
                              Meeting link will be provided after registration
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h4 className="font-medium text-[#202020] mb-6 text-sm uppercase tracking-wider">What you&apos;ll get:</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">Personalised feedback and guidance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">Learn with other parents who share your perspective</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">Maintain accountability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">Coach led weekly training discipline for 5x better results</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">One year access to the Contemplate app</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">Join the Contemplate Parents community and attend relevant events throughout the year</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-lg bg-[#202020]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#202020]" />
                    </div>
                    <span className="text-[#202020]/70">Discount on coach certification program if you apply and get selected (live session participants are strongly preferred for our coach certification program)</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <Button
                onClick={() => {
                  // Track button click
                  if (window.gtag) {
                    window.gtag('event', 'click', {
                      event_category: 'CTA',
                      event_label: 'Join Guided Cohort'
                    });
                  }
                  if (window.fbq) {
                    window.fbq('track', 'Lead');
                  }
                  // Navigate to checkout with product type
                  navigate('/checkout?productType=cohort');
                }}
                className="w-full bg-[#202020] hover:bg-[#303030] text-[#efede6] py-6 rounded-lg text-base font-medium transition-all hover:scale-[1.02] group"
              >
                Join the Guided Cohort
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              {/* Guarantee */}
              <div className="flex items-center justify-center gap-2 mt-6 text-[#202020]/40">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Cancel anytime — No contracts</span>
              </div>

              {/* Partner Discount Note */}
              <div className="flex items-center justify-center gap-2 mt-4 text-[#202020]/50">
                <Users className="w-4 h-4" />
                <span className="text-sm">Add your partner at checkout for <strong>50% off</strong> their fee</span>
              </div>

              {/* Trainer Video */}
              <div className="my-16">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-normal text-[#202020] mb-4 tracking-tight">
                    How will the live cohort training work?
                  </h3>
                  <p className="text-lg text-[#202020]/70 mb-6">
                    Tarun Gulati, founder of Contemplate will conduct this live cohort. Watch this video by him to understand how it will be conducted.
                  </p>
                </div>
                <div className="relative w-full max-w-[800px] mx-auto rounded-2xl overflow-hidden shadow-2xl bg-[#202020] aspect-video">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/pzSgYgPVG78"
                    title="Contemplate Trainer Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              <div className="my-16">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-normal text-[#202020] mb-4 tracking-tight">
                    Sample live cohort training session
                  </h3>
                  <p className="text-lg text-[#202020]/70 mb-6">
                    This is an actual live cohort training session from 2025. This will help you understand the kind of live training you will go through.
                  </p>
                </div>
                <div className="relative w-full max-w-[800px] mx-auto rounded-2xl overflow-hidden shadow-2xl bg-[#202020] aspect-video">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/OXxCZjKPAS4"
                    title="Sample live cohort training session"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
